import {useState} from "react";
import {App as AntdApp, Tag, Descriptions} from "antd";
import className from "classnames/bind";
import {SearchBar, Button, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useInspectGscUrlMutation} from "~/reduxs/api/gscApiSlice";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);

/** Thẻ verdict của Google (PASS/PARTIAL/FAIL/NEUTRAL) → Tag màu + nhãn Việt. */
function VerdictTag({value}) {
	const map = {
		PASS: ['green', 'Đạt'],
		PARTIAL: ['orange', 'Một phần'],
		FAIL: ['red', 'Có vấn đề'],
		NEUTRAL: ['default', 'Trung tính'],
	};
	const [color, label] = map[value] || ['default', value || '—'];
	return <Tag color={color}>{label}</Tag>;
}

/** Panel kết quả URL Inspection. */
function ResultCard({result, onRefresh}) {
	const idxItems = [
		{key: 'coverage', label: 'Trạng thái', children: <span><VerdictTag value={result.verdict} /> {result.coverage_state || '—'}</span>},
		{key: 'indexing', label: 'Cho phép index', children: result.indexing_state || '—'},
		{key: 'robots', label: 'robots.txt', children: result.robots_txt_state || '—'},
		{key: 'fetch', label: 'Tải trang', children: result.page_fetch_state || '—'},
		{key: 'crawledAs', label: 'Bot crawl', children: result.crawled_as || '—'},
		{key: 'lastCrawl', label: 'Lần crawl cuối', children: result.last_crawl_time || 'Chưa crawl'},
		{key: 'gcanon', label: 'Canonical (Google chọn)', children: <span className={cn('breakAll')}>{result.google_canonical || '—'}</span>},
		{key: 'ucanon', label: 'Canonical (khai báo)', children: <span className={cn('breakAll')}>{result.user_canonical || '—'}</span>},
	];

	return (
		<div className={cn('section')}>
			<div className={cn('rangeBar')} style={{marginBottom: 12}}>
				<div className={cn('breakAll')} style={{fontWeight: 600}}>{result.url}</div>
				<div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
					{result.inspection_link && (
						<a href={result.inspection_link} target="_blank" rel="noreferrer" className={cn('metaRow')}>
							<FontAwesomeIcon icon="fa-brands fa-google" /> Mở trong GSC
						</a>
					)}
					<Button white small onClick={onRefresh}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-arrows-rotate" />}>Kiểm tra lại</Button>
				</div>
			</div>

			{result.cached && (
				<p className={cn('metaRow')}>
					<FontAwesomeIcon icon="fa-light fa-clock-rotate-left" /> Kết quả đã lưu lúc {result.inspected_at} — bấm "Kiểm tra lại" để cập nhật.
				</p>
			)}

			<Descriptions size="small" column={{xs: 1, sm: 2}} bordered items={idxItems} />

			<div className={cn('inspectGrid')}>
				<div>
					<h4 className={cn('sectionTitle')}>Thân thiện di động <VerdictTag value={result.mobile?.verdict} /></h4>
					{(result.mobile?.issues || []).length === 0
						? <p className={cn('metaRow')}>Không có vấn đề.</p>
						: <ul className={cn('issueList')}>{result.mobile.issues.map((m, i) => <li key={i}>{m}</li>)}</ul>}
				</div>
				<div>
					<h4 className={cn('sectionTitle')}>Kết quả nhiều định dạng (Rich results) <VerdictTag value={result.rich_results?.verdict} /></h4>
					{(result.rich_results?.items || []).length === 0
						? <p className={cn('metaRow')}>Không phát hiện loại rich result.</p>
						: <div>{result.rich_results.items.map((t, i) => <Tag key={i}>{t}</Tag>)}</div>}
				</div>
			</div>

			{(result.sitemaps || []).length > 0 && (
				<>
					<h4 className={cn('sectionTitle')}>Sitemap tham chiếu</h4>
					<ul className={cn('issueList')}>{result.sitemaps.map((s, i) => <li key={i} className={cn('breakAll')}>{s}</li>)}</ul>
				</>
			)}

			{(result.referring_urls || []).length > 0 && (
				<>
					<h4 className={cn('sectionTitle')}>URL liên kết tới (mẫu)</h4>
					<ul className={cn('issueList')}>{result.referring_urls.slice(0, 10).map((u, i) => <li key={i} className={cn('breakAll')}>{u}</li>)}</ul>
				</>
			)}
		</div>
	);
}

/**
 * Tab "Kiểm tra URL" — URL Inspection API (on-demand). Nhập 1 URL thuộc property → gọi
 * Google lấy trạng thái index/coverage/canonical/mobile/rich results (có cache DB 6h).
 */
function GscUrlInspect({projectId, propertyUrl}) {

	const {notification} = AntdApp.useApp();
	const [inspect, {isLoading}] = useInspectGscUrlMutation();
	const [result, setResult] = useState(null);

	const run = async (url, refresh = false) => {
		try {
			const data = await inspect({projectId, url, refresh}).unwrap();
			setResult(data);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không kiểm tra được URL')});
		}
	};

	const sample = propertyUrl?.startsWith('sc-domain:')
		? `https://${propertyUrl.slice(10)}/trang-can-kiem-tra`
		: `${propertyUrl || ''}trang-can-kiem-tra`;

	return (
		<div>
			<p className={cn('metaRow')}>
				Nhập URL thuộc property <b>{propertyUrl}</b> để xem trạng thái index thật từ Google (URL Inspection).
				Kết quả được lưu 6 giờ để tiết kiệm quota (~2000 lần/ngày/property).
			</p>

			<SearchBar
				icon="fa-light fa-magnifying-glass"
				placeholder={`Ví dụ: ${sample}`}
				buttonText="Kiểm tra"
				buttonIcon="fa-light fa-magnifying-glass"
				loading={isLoading}
				onSubmit={(v) => run(v, false)}
			/>

			{isLoading && (
				<div className={cn('section')}><span className="app-spinner" /> Đang hỏi Google…</div>
			)}

			{!isLoading && result && <ResultCard result={result} onRefresh={() => run(result.url, true)} />}
		</div>
	);
}

export default GscUrlInspect;
